import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useFacebookPageAutoCompleteViewModel } from '../../../../queries/Social/useFacebookPageAutoCompleteViewModel';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { Pill } from '../../../components-v2/Pill';
import { TextArea } from '../../../components-v2/TextArea';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { AutoCompleteDropdown } from '../../autocomplete/AutoCompleteDropdown';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { SocialMediaImageSearchAndDisplay } from '../SocialMediaImageSearchAndDisplay';
import { SocialMediaImagesPreviewModal } from '../SocialMediaImagesPreviewModal';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	headerLabel?: string;
	imagesLabel?: string;
	impersonationContext?: ImpersonationContextViewModel;
	messageLabel?: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IFacebookMentionTrigger {
	end: number;
	query: string;
	start: number;
}

interface IAnchorPosition {
	left: number;
	top: number;
}

const FACEBOOK_MENTION_TRIGGER_REGEX = /(^|\s)@(\S{0,2048})$/;
const FACEBOOK_VERIFICATION_BADGES = {
	blue_verified: { color: 'blue', label: 'Verified' },
	gray_verified: { color: 'gray', label: 'Verified' },
} as const;

const FacebookVerificationStatusBadge: React.FC<{ verificationStatus?: string }> = ({ verificationStatus }) => {
	const normalizedStatus = verificationStatus?.trim().toLowerCase();
	const badge = normalizedStatus
		? FACEBOOK_VERIFICATION_BADGES[normalizedStatus as keyof typeof FACEBOOK_VERIFICATION_BADGES]
		: undefined;

	return badge ? <Pill color={badge.color} label={badge.label} size='small' variant='label' /> : null;
};

function getFacebookMentionTrigger(value: string, caretIndex: number): IFacebookMentionTrigger | null {
	const textBeforeCaret = value.slice(0, caretIndex);
	const triggerMatch = textBeforeCaret.match(FACEBOOK_MENTION_TRIGGER_REGEX);
	if (!triggerMatch) {
		return null;
	}

	const query = triggerMatch[2] || '';
	return {
		end: caretIndex,
		query,
		start: textBeforeCaret.length - query.length - 1,
	};
}

function getTextareaAnchorPosition(textArea: HTMLTextAreaElement): IAnchorPosition {
	const computedStyle = window.getComputedStyle(textArea);
	const mirror = document.createElement('div');
	const marker = document.createElement('span');
	const textBeforeCaret = textArea.value.slice(0, textArea.selectionStart || 0);
	const textAfterCaret = textArea.value.slice(textArea.selectionStart || 0);

	const mirroredProperties = [
		'borderBottomWidth',
		'borderLeftWidth',
		'borderRightWidth',
		'borderTopWidth',
		'boxSizing',
		'fontFamily',
		'fontSize',
		'fontStyle',
		'fontVariant',
		'fontWeight',
		'letterSpacing',
		'lineHeight',
		'paddingBottom',
		'paddingLeft',
		'paddingRight',
		'paddingTop',
		'textAlign',
		'textIndent',
		'whiteSpace',
		'wordBreak',
		'wordSpacing',
		'wordWrap',
	] as const;

	mirror.style.position = 'absolute';
	mirror.style.visibility = 'hidden';
	mirror.style.whiteSpace = 'pre-wrap';
	mirror.style.wordWrap = 'break-word';
	mirror.style.overflow = 'hidden';
	mirror.style.width = computedStyle.width;

	mirroredProperties.forEach(property => {
		mirror.style[property] = computedStyle[property];
	});

	mirror.textContent = textBeforeCaret;
	marker.textContent = textAfterCaret || '.';
	mirror.appendChild(marker);
	textArea.ownerDocument.body.appendChild(mirror);

	const anchorPosition = {
		left: Math.max(12, marker.offsetLeft - textArea.scrollLeft),
		top: Math.max(12, marker.offsetTop - textArea.scrollTop + marker.offsetHeight),
	};

	textArea.ownerDocument.body.removeChild(mirror);
	return anchorPosition;
}

function getFacebookMentionText(mention: Api.IFacebookPageMention, content?: string) {
	if (mention.pageName) {
		return `@${mention.pageName}`;
	}

	if (!content) {
		return '';
	}

	return content.substring(mention.contentOffset, mention.contentOffset + mention.contentLength);
}

function getContentChangeRange(previousContent: string, nextContent: string) {
	let start = 0;
	while (
		start < previousContent.length &&
		start < nextContent.length &&
		previousContent[start] === nextContent[start]
	) {
		start += 1;
	}

	let previousEnd = previousContent.length;
	let nextEnd = nextContent.length;
	while (previousEnd > start && nextEnd > start && previousContent[previousEnd - 1] === nextContent[nextEnd - 1]) {
		previousEnd -= 1;
		nextEnd -= 1;
	}

	return {
		nextEnd,
		previousEnd,
		start,
	};
}

function reconcileFacebookMentions(
	previousContent: string,
	nextContent: string,
	facebookMentions: Api.IFacebookPageMention[] | undefined
): Api.IFacebookPageMention[] {
	if (!facebookMentions?.length) {
		return [];
	}

	const { start, previousEnd, nextEnd } = getContentChangeRange(previousContent, nextContent);
	const offsetDelta = nextEnd - previousEnd;

	return facebookMentions
		.map(mention => {
			const mentionText = getFacebookMentionText(mention, previousContent);
			if (!mentionText) {
				return null;
			}

			const mentionStart = mention.contentOffset;
			const mentionEnd = mentionStart + mention.contentLength;
			const overlapsChangedRange = mentionStart < previousEnd && mentionEnd > start;
			if (overlapsChangedRange) {
				return null;
			}

			const nextOffset = mentionStart >= previousEnd ? mentionStart + offsetDelta : mentionStart;
			const actualMentionText = nextContent.substring(nextOffset, nextOffset + mentionText.length);
			if (actualMentionText !== mentionText) {
				return null;
			}

			return {
				...mention,
				contentLength: mentionText.length,
				contentOffset: nextOffset,
			};
		})
		.filter((mention): mention is Api.IFacebookPageMention => Boolean(mention));
}

export const SocialMediaPostContentEditor: React.FC<IProps> = props => {
	const {
		className,
		headerLabel,
		imagesLabel,
		impersonationContext,
		messageLabel,
		onPostScheduled,
		post,
		readOnly,
		styles = [],
	} = props;
	const {
		postContent,
		postImages,
		setContent,
		setImages: _setImages,
		hasVideo,
		overlayLogo,
		hideWhiteBackground,
		logoSize,
		setLogoSize,
		facebookMentions,
		setFacebookMentions,
		postTargets,
	} = useEditSocialMediaPostContext();

	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession.account;
	const isFacebookMentioningEnabled = Boolean(
		account?.features?.socialMedia?.facebookMentionsEnabled &&
			postTargets?.some(target => target.provider === Api.SocialMediaType.Facebook) &&
			!readOnly
	);
	const inputRef = React.useRef<HTMLTextAreaElement>(null);
	const textAreaWrapperRef = React.useRef<HTMLDivElement>(null);
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');
	const [facebookMentionTrigger, setFacebookMentionTrigger] = React.useState<IFacebookMentionTrigger | null>(null);
	const [facebookMentionAnchorPosition, setFacebookMentionAnchorPosition] = React.useState<IAnchorPosition>({
		left: 14,
		top: 36,
	});
	const facebookPageAutoComplete = useFacebookPageAutoCompleteViewModel({
		enabled: isFacebookMentioningEnabled,
		impersonationContext: impersonationContext?.toJs(),
	});

	const onInputRef = (ref: HTMLTextAreaElement) => {
		inputRef.current = ref;
	};

	const syncFacebookMentionTrigger = React.useCallback(
		(content: string, selectionStart?: number | null) => {
			if (
				!isFacebookMentioningEnabled ||
				selectionStart === null ||
				selectionStart === undefined ||
				!inputRef.current
			) {
				setFacebookMentionTrigger(null);
				return;
			}

			const nextTrigger = getFacebookMentionTrigger(content, selectionStart);
			if (!nextTrigger) {
				setFacebookMentionTrigger(null);
				return;
			}

			setFacebookMentionTrigger(nextTrigger);
			setFacebookMentionAnchorPosition(getTextareaAnchorPosition(inputRef.current));
		},
		[isFacebookMentioningEnabled]
	);

	const syncFacebookMentionContext = React.useCallback(
		(previousContent: string, nextContent: string) => {
			const nextMentions = reconcileFacebookMentions(previousContent, nextContent, facebookMentions);
			const mentionsChanged =
				nextMentions.length !== (facebookMentions || []).length ||
				nextMentions.some((mention, index) => {
					const currentMention = facebookMentions?.[index];
					return (
						!currentMention ||
						currentMention.contentOffset !== mention.contentOffset ||
						currentMention.contentLength !== mention.contentLength ||
						currentMention.pageId !== mention.pageId ||
						currentMention.pageName !== mention.pageName ||
						currentMention.handle !== mention.handle
					);
				});
			if (mentionsChanged) {
				setFacebookMentions(nextMentions);
			}
		},
		[facebookMentions, setFacebookMentions]
	);

	const onTextInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const nextContent = e.target.value;
			setContent(nextContent);
			syncFacebookMentionContext(postContent || '', nextContent);
			syncFacebookMentionTrigger(nextContent, e.target.selectionStart);
		},
		[postContent, setContent, syncFacebookMentionContext, syncFacebookMentionTrigger]
	);

	const onTextAreaSelectionChanged = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLTextAreaElement>) => {
			if (!facebookMentionTrigger) {
				return;
			}

			const target = e.target as HTMLTextAreaElement;
			syncFacebookMentionTrigger(target.value, target.selectionStart);
		},
		[facebookMentionTrigger, syncFacebookMentionTrigger]
	);

	const onClickEnlargeImage = (initialUrl: string) => {
		setInitialImageUrl(initialUrl);
		setImagePreviewModalOpen(true);
	};

	const onEmojiClick = React.useCallback(
		(_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
			if (!inputRef.current) {
				return;
			}

			const previousContent = postContent || '';
			const selectionStart = inputRef.current.selectionStart || 0;
			const nextContent =
				previousContent.substring(0, selectionStart) +
				data.emoji +
				previousContent.substring(inputRef.current.selectionStart);
			const nextSelectionStart = selectionStart + data.emoji.length;

			setContent(nextContent);
			syncFacebookMentionContext(previousContent, nextContent);

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
				syncFacebookMentionTrigger(nextContent, nextSelectionStart);
			});
		},
		[postContent, setContent, syncFacebookMentionContext, syncFacebookMentionTrigger]
	);

	const insertFacebookPageMention = React.useCallback(
		(page: Api.IFacebookPageLookupResult, start: number, end: number) => {
			const content = postContent || '';
			const safeStart = Math.max(0, Math.min(start, content.length));
			const safeEnd = Math.max(safeStart, Math.min(end, content.length));
			const mentionText = `@${page.pageName || ''}`;
			const nextContent = content.slice(0, safeStart) + mentionText + content.slice(safeEnd);
			const offsetDelta = mentionText.length - (safeEnd - safeStart);
			const nextSelectionStart = safeStart + mentionText.length;

			setContent(nextContent);
			setFacebookMentions(currentMentions => {
				const nextMentions = (currentMentions || [])
					.filter(mention => {
						const mentionEnd = mention.contentOffset + mention.contentLength;
						return mentionEnd <= safeStart || mention.contentOffset >= safeEnd;
					})
					.map(mention => {
						if (mention.contentOffset >= safeEnd) {
							return {
								...mention,
								contentOffset: mention.contentOffset + offsetDelta,
							};
						}

						return mention;
					});

				return [
					...nextMentions,
					{
						contentLength: mentionText.length,
						contentOffset: safeStart,
						handle: page.handle,
						pageId: page.pageId,
						pageName: page.pageName,
					},
				].sort((left, right) => left.contentOffset - right.contentOffset);
			});
			setFacebookMentionTrigger(null);
			facebookPageAutoComplete.reset();

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
			});
		},
		[facebookPageAutoComplete, postContent, setContent, setFacebookMentions]
	);

	const onFacebookPageSelected = React.useCallback(
		(page: Api.IFacebookPageLookupResult) => {
			if (!facebookMentionTrigger) {
				return;
			}

			insertFacebookPageMention(page, facebookMentionTrigger.start, facebookMentionTrigger.end);
		},
		[facebookMentionTrigger, insertFacebookPageMention]
	);

	const onValidateSelectedFile = React.useCallback(
		(file: File) => {
			const mimeType = file.type?.toLocaleLowerCase();
			if (!(mimeType.startsWith('video/') || mimeType.startsWith('image/'))) {
				toast.error('Please select a valid image or video file.');
				return false;
			}

			if (mimeType.startsWith('video/') && !hasVideo && postImages?.length > 0) {
				toast.error('This post already has a image. Please remove the image to add a video.');
				return false;
			}

			if (mimeType.startsWith('image/') && hasVideo) {
				toast.error('This post already has a video. Please remove the video to add an image.');
				return false;
			}

			return true;
		},
		[hasVideo, postImages?.length]
	);

	React.useEffect(() => {
		if (isFacebookMentioningEnabled) {
			return;
		}

		setFacebookMentionTrigger(null);
	}, [isFacebookMentioningEnabled]);

	const mentionDropdownSearchQuery = facebookMentionTrigger ? facebookMentionTrigger.query || ' ' : '';
	const normalizedMentionSearch = facebookMentionTrigger?.query?.trim() || '';

	return (
		<div className={`${css(styleSheet.container, ...styles)} social-media-post-content-editor ${className || ''}`}>
			<div className={css(styleSheet.headerContainer)}>
				{headerLabel && !readOnly && <div className={css(styleSheet.header)}>{headerLabel}</div>}
			</div>
			<div className={css(styleSheet.composerContainer)}>
				<div className={css(styleSheet.labelContainer)}>
					<div className={`emoji-label-container ${css(styleSheet.labelEmoji)}`}>
						{messageLabel && <label>{messageLabel}</label>}
					</div>
					<div className={css(styleSheet.charCount)}>
						<EmojiPickerButton onEmojiClick={!readOnly ? onEmojiClick : null} popoverPlacement={['left', 'top']} />
						{postContent?.length > 0 ? `${postContent?.length}/2200` : '2200'}
					</div>
				</div>
				<div className={css(styleSheet.textAreaWrapper)} ref={textAreaWrapperRef}>
					<TextArea
						id='social-media-content'
						disabled={readOnly}
						styles={[styleSheet.textAreaContainer]}
						onChange={onTextInputChanged}
						onClick={onTextAreaSelectionChanged}
						onKeyUp={onTextAreaSelectionChanged}
						ref={onInputRef}
						value={postContent || ''}
					/>
					{isFacebookMentioningEnabled && facebookMentionTrigger ? (
						<AutoCompleteDropdown<Api.IFacebookPageLookupResult, Api.IFacebookPageLookupResult>
							anchor={() => (
								<span
									aria-hidden='true'
									className={css(styleSheet.mentionAnchor)}
									style={{
										left: `${facebookMentionAnchorPosition.left}px`,
										top: `${facebookMentionAnchorPosition.top}px`,
									}}
								/>
							)}
							autoCompleteViewModel={facebookPageAutoComplete}
							contentPositionY='bottom'
							dropdownContentItemsStyles={[styleSheet.mentionDropdown]}
							isOpen={true}
							loaderType='small'
							onItemSelected={onFacebookPageSelected}
							onRenderEmptyResults={() => (
								<div className={css(styleSheet.mentionEmptyState)}>
									{normalizedMentionSearch
										? `No Facebook Pages found for @${normalizedMentionSearch}.`
										: 'Type a Facebook Page handle or paste a Facebook Page URL.'}
								</div>
							)}
							onRenderItem={(item, index, highlighted, onClick) => (
								<button
									className={css(styleSheet.mentionResultItem, highlighted && styleSheet.mentionResultItemHighlighted)}
									key={`${item.pageId || item.handle || 'facebook-page'}-${index}`}
									onMouseDown={e => e.preventDefault()}
									type='button'
									onClick={() => onClick()}
								>
									<div className={css(styleSheet.mentionResultNameRow)}>
										<div className={css(styleSheet.mentionResultName)}>{item.pageName}</div>
										<FacebookVerificationStatusBadge verificationStatus={item.verificationStatus} />
									</div>
									{item.handle ? <div className={css(styleSheet.mentionResultHandle)}>@{item.handle}</div> : null}
								</button>
							)}
							searchQuery={mentionDropdownSearchQuery}
						/>
					) : null}
				</div>
			</div>

			<div className={`add-media-container ${css(styleSheet.mediaContainer)}`}>
				<label>{imagesLabel && !readOnly ? imagesLabel : 'Media'}</label>
				<SocialMediaImageSearchAndDisplay
					onClickEnlargeImage={onClickEnlargeImage}
					hasVideo={hasVideo}
					readOnly={readOnly}
					onValidateSelectedFile={onValidateSelectedFile}
					post={post}
					onPostScheduled={onPostScheduled}
				/>
			</div>

			<SocialMediaImagesPreviewModal
				modalProps={{
					isOpen: imagePreviewModalOpen,
					onRequestClose: () => setImagePreviewModalOpen(false),
				}}
				initialImageUrl={initialImageUrl || ''}
				images={postImages}
				showOverlayLogo={overlayLogo}
				logoUrl={account?.preferences?.logo?.url || ''}
				hideWhiteBackground={hideWhiteBackground}
				logoSize={logoSize}
				onLogoSizeChange={setLogoSize}
			/>
		</div>
	);
};
